// src/components/employee/EmployeeTax.js
import React, { useState, useEffect } from 'react';
import {
    Receipt, Download, Calendar, Info, FileText,
    TrendingUp, Loader, AlertCircle, DollarSign, Shield,
    PieChart, ChevronDown
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFlexiRequests } from '../../contexts/FlexiRequestContext';
import { api } from '../../services/api';

const EmployeeTax = () => {
    const { theme } = useTheme();
    const { currentUser } = useFlexiRequests();

    const [activeTab, setActiveTab] = useState('TDS SUMMARY');
    const [selectedYear, setSelectedYear] = useState('2024-25');
    const [loading, setLoading] = useState(false);
    const [tdsHistory, setTdsHistory] = useState([]);
    const [ytdSummary, setYtdSummary] = useState(null);
    const [form16Status, setForm16Status] = useState(null);
    const [error, setError] = useState(null);

    // Financial years for selection
    const financialYears = ['2024-25', '2023-24', '2022-23', '2021-22'];

    // Tab sections
    const tabSections = ['TDS SUMMARY', 'TDS HISTORY', 'TAX FORMS'];

    // Fetch TDS History
    const fetchTDSHistory = async (financialYear) => {
        if (!currentUser?.id) return;

        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/tds/history/${currentUser.id}`, {
                params: { financialYear }
            });
            if (response.data.success) {
                setTdsHistory(response.data.data.history || []);
            }
        } catch (err) {
            console.error('Error fetching TDS history:', err);
            setError('Failed to load TDS history. Please try again later.');
            setTdsHistory([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch YTD TDS Summary
    const fetchYTDSummary = async (financialYear) => {
        if (!currentUser?.id) return;

        setLoading(true);
        setError(null);
        try {
            const response = await api.get(`/api/tds/ytd/${currentUser.id}`, {
                params: { financialYear }
            });
            if (response.data.success) {
                setYtdSummary(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching YTD summary:', err);
            setError('Failed to load TDS summary. Please try again later.');
            setYtdSummary({
                financial_year: financialYear,
                total_months: 0,
                total_gross_salary: 0,
                total_taxable_income: 0,
                total_standard_deduction: 0,
                total_tds_deducted: 0,
                avg_monthly_tds: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // Check Form 16 availability
    const checkForm16Status = async (financialYear) => {
        try {
            // This would be a real API call in production
            setForm16Status({
                available: false,
                message: 'Form 16 will be available after March 31st'
            });
        } catch (err) {
            console.error('Error checking Form 16 status:', err);
        }
    };

    // Initial data load
    useEffect(() => {
        if (currentUser?.id) {
            fetchTDSHistory(selectedYear);
            fetchYTDSummary(selectedYear);
            checkForm16Status(selectedYear);
        }
    }, [currentUser?.id, selectedYear]);

    // Download TDS Statement
    const downloadTDSStatement = () => {
        if (!tdsHistory || tdsHistory.length === 0) {
            alert('No TDS data available to download');
            return;
        }

        // Create CSV content
        const csvHeaders = [
            'Month/Year',
            'Gross Salary',
            'Standard Deduction',
            'Professional Tax',
            'Taxable Income',
            'Tax Slab',
            'TDS Deducted',
            'Exemption Applied',
            'Notes'
        ];

        const csvRows = tdsHistory.map(entry => [
            entry.month_year,
            entry.gross_salary || 0,
            entry.standard_deduction || 0,
            entry.professional_tax || 0,
            entry.taxable_income || 0,
            entry.tax_slab_label || 'N/A',
            entry.tds_amount || 0,
            entry.is_exempted ? 'Yes' : 'No',
            entry.notes || ''
        ]);

        const csvContent = [
            csvHeaders.join(','),
            ...csvRows.map(row => row.join(','))
        ].join('\n');

        // Create download link
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `TDS_Statement_${selectedYear}_${currentUser.name?.replace(/\s+/g, '_')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `₹${parseFloat(amount).toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    // Render TDS Summary Tab
    const renderSummaryTab = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-500" />
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Year-to-Date Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Total Gross Salary */}
                    <div className={`p-6 rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <DollarSign className={`w-8 h-8 ${
                                theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`} />
                        </div>
                        <h3 className={`text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Total Gross Salary (YTD)
                        </h3>
                        <p className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {formatCurrency(ytdSummary?.total_gross_salary || 0)}
                        </p>
                        <p className={`text-xs mt-2 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                            {ytdSummary?.total_months || 0} months
                        </p>
                    </div>

                    {/* Total Taxable Income */}
                    <div className={`p-6 rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <Receipt className={`w-8 h-8 ${
                                theme === 'dark' ? 'text-purple-400' : 'text-purple-600'
                            }`} />
                        </div>
                        <h3 className={`text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Taxable Income (YTD)
                        </h3>
                        <p className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {formatCurrency(ytdSummary?.total_taxable_income || 0)}
                        </p>
                        <p className={`text-xs mt-2 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                            After deductions
                        </p>
                    </div>

                    {/* Total TDS Deducted */}
                    <div className={`p-6 rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-700/30'
                            : 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                    }`}>
                        <div className="flex items-center justify-between mb-3">
                            <Shield className={`w-8 h-8 ${
                                theme === 'dark' ? 'text-red-400' : 'text-red-600'
                            }`} />
                        </div>
                        <h3 className={`text-sm font-medium mb-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Total TDS Deducted (YTD)
                        </h3>
                        <p className={`text-2xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            {formatCurrency(ytdSummary?.total_tds_deducted || 0)}
                        </p>
                        <p className={`text-xs mt-2 ${
                            theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                            Avg: {formatCurrency(ytdSummary?.avg_monthly_tds || 0)}/month
                        </p>
                    </div>
                </div>

                {/* Deductions Breakdown */}
                <div className={`p-6 rounded-lg border ${
                    theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                }`}>
                    <h3 className={`text-lg font-semibold mb-4 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Tax Deductions Breakdown
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                Standard Deduction (₹50,000)
                            </span>
                            <span className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {formatCurrency(ytdSummary?.total_standard_deduction || 0)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                Professional Tax
                            </span>
                            <span className={`font-semibold ${
                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                            }`}>
                                {formatCurrency(ytdSummary?.total_professional_tax || 0)}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Tax Information */}
                <div className={`p-6 rounded-lg border ${
                    theme === 'dark'
                        ? 'bg-blue-900/20 border-blue-700/30'
                        : 'bg-blue-50 border-blue-200'
                }`}>
                    <div className="flex items-start gap-3">
                        <Info className={`w-5 h-5 mt-0.5 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <div className="space-y-2">
                            <h4 className={`font-semibold ${
                                theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
                            }`}>
                                About TDS (Tax Deducted at Source)
                            </h4>
                            <ul className={`text-sm space-y-1 ${
                                theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
                            }`}>
                                <li>• TDS is automatically deducted from your monthly salary based on your annual income projection</li>
                                <li>• Standard deduction of ₹50,000 per year is automatically applied</li>
                                <li>• Professional Tax paid is deducted from taxable income</li>
                                <li>• Tax slabs are as per Income Tax Act, 1961 (New Tax Regime by default)</li>
                                <li>• Form 16 will be issued at the end of the financial year for ITR filing</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Render TDS History Tab
    const renderHistoryTab = () => {
        if (loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-500" />
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {/* Year Selector and Download */}
                <div className="flex items-center justify-between">
                    <div className="relative">
                        <select
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                            className={`px-4 py-2 pr-10 rounded-lg border appearance-none cursor-pointer ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700 text-white'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            {financialYears.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                        <ChevronDown className={`absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`} />
                    </div>

                    <button
                        onClick={downloadTDSStatement}
                        disabled={!tdsHistory || tdsHistory.length === 0}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                            !tdsHistory || tdsHistory.length === 0
                                ? 'opacity-50 cursor-not-allowed'
                                : ''
                        } ${
                            theme === 'dark'
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        <Download className="w-4 h-4" />
                        Download Statement
                    </button>
                </div>

                {/* TDS History Table */}
                {tdsHistory && tdsHistory.length > 0 ? (
                    <div className={`overflow-x-auto rounded-lg border ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <table className="w-full">
                            <thead className={theme === 'dark' ? 'bg-gray-800' : 'bg-gray-50'}>
                                <tr>
                                    <th className={`px-4 py-3 text-left text-xs font-semibold ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Month/Year</th>
                                    <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Gross Salary</th>
                                    <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Taxable Income</th>
                                    <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Tax Slab</th>
                                    <th className={`px-4 py-3 text-right text-xs font-semibold ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>TDS Deducted</th>
                                    <th className={`px-4 py-3 text-center text-xs font-semibold ${
                                        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                    }`}>Status</th>
                                </tr>
                            </thead>
                            <tbody className={`divide-y ${
                                theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'
                            }`}>
                                {tdsHistory.map((entry, index) => (
                                    <tr key={index} className={
                                        theme === 'dark' ? 'bg-gray-800/50' : 'bg-white'
                                    }>
                                        <td className={`px-4 py-3 text-sm ${
                                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {entry.month_year}
                                        </td>
                                        <td className={`px-4 py-3 text-sm text-right ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            {formatCurrency(entry.gross_salary)}
                                        </td>
                                        <td className={`px-4 py-3 text-sm text-right ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                        }`}>
                                            {formatCurrency(entry.taxable_income)}
                                        </td>
                                        <td className={`px-4 py-3 text-sm text-right ${
                                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                        }`}>
                                            {entry.tax_slab_label || 'N/A'}
                                        </td>
                                        <td className={`px-4 py-3 text-sm text-right font-semibold ${
                                            theme === 'dark' ? 'text-red-400' : 'text-red-600'
                                        }`}>
                                            {formatCurrency(entry.tds_amount)}
                                        </td>
                                        <td className={`px-4 py-3 text-center`}>
                                            {entry.is_exempted ? (
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    theme === 'dark'
                                                        ? 'bg-green-900/30 text-green-400'
                                                        : 'bg-green-100 text-green-700'
                                                }`}>
                                                    Exempted
                                                </span>
                                            ) : (
                                                <span className={`px-2 py-1 text-xs rounded-full ${
                                                    theme === 'dark'
                                                        ? 'bg-blue-900/30 text-blue-400'
                                                        : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                    Deducted
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className={`p-8 text-center rounded-lg border ${
                        theme === 'dark'
                            ? 'bg-gray-800 border-gray-700'
                            : 'bg-white border-gray-200'
                    }`}>
                        <FileText className={`w-12 h-12 mx-auto mb-3 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                        }`} />
                        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                            No TDS deductions found for {selectedYear}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    // Render Tax Forms Tab
    const renderTaxFormsTab = () => {
        return (
            <div className="space-y-6">
                {/* Form 16 Card */}
                <div className={`p-6 rounded-lg border ${
                    theme === 'dark'
                        ? 'bg-gray-800 border-gray-700'
                        : 'bg-white border-gray-200'
                }`}>
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-lg ${
                                theme === 'dark' ? 'bg-blue-900/30' : 'bg-blue-100'
                            }`}>
                                <FileText className={`w-6 h-6 ${
                                    theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                                }`} />
                            </div>
                            <div>
                                <h3 className={`text-lg font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Form 16 - TDS Certificate
                                </h3>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Annual certificate for tax deducted at source
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className={`p-4 rounded-lg mb-4 ${
                        theme === 'dark'
                            ? 'bg-yellow-900/20 border border-yellow-700/30'
                            : 'bg-yellow-50 border border-yellow-200'
                    }`}>
                        <p className={`text-sm ${
                            theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
                        }`}>
                            {form16Status?.message || 'Form 16 will be available after the financial year ends'}
                        </p>
                    </div>

                    <button
                        disabled={!form16Status?.available}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                            form16Status?.available
                                ? theme === 'dark'
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                                : theme === 'dark'
                                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        <Download className="w-4 h-4" />
                        Download Form 16
                    </button>
                </div>

                {/* Tax Regime Information */}
                <div className={`p-6 rounded-lg border ${
                    theme === 'dark'
                        ? 'bg-blue-900/20 border-blue-700/30'
                        : 'bg-blue-50 border-blue-200'
                }`}>
                    <div className="flex items-start gap-3">
                        <Info className={`w-5 h-5 mt-0.5 ${
                            theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                        }`} />
                        <div className="space-y-2">
                            <h4 className={`font-semibold ${
                                theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
                            }`}>
                                New Tax Regime (Default)
                            </h4>
                            <div className={`text-sm space-y-2 ${
                                theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
                            }`}>
                                <p>Your TDS is calculated using the New Tax Regime with the following slabs:</p>
                                <ul className="space-y-1 ml-4">
                                    <li>• Up to ₹3,00,000: Nil</li>
                                    <li>• ₹3,00,001 to ₹6,00,000: 5%</li>
                                    <li>• ₹6,00,001 to ₹9,00,000: 10%</li>
                                    <li>• ₹9,00,001 to ₹12,00,000: 15%</li>
                                    <li>• ₹12,00,001 to ₹15,00,000: 20%</li>
                                    <li>• Above ₹15,00,000: 30%</li>
                                </ul>
                                <p className="mt-2">
                                    <strong>Note:</strong> Contact HR if you wish to opt for the Old Tax Regime with deductions.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={`min-h-screen p-6 ${
            theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className={`text-2xl font-bold mb-2 ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                        Tax Management (TDS)
                    </h1>
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        View your TDS deductions, tax summary, and download tax certificates
                    </p>
                </div>

                {/* Error Alert */}
                {error && (
                    <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${
                        theme === 'dark'
                            ? 'bg-red-900/20 border-red-700/30 text-red-400'
                            : 'bg-red-50 border-red-200 text-red-700'
                    }`}>
                        <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                        <p>{error}</p>
                    </div>
                )}

                {/* Tabs */}
                <div className={`border-b mb-6 ${
                    theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}>
                    <div className="flex gap-1 overflow-x-auto">
                        {tabSections.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                                    activeTab === tab
                                        ? theme === 'dark'
                                            ? 'text-blue-400 border-b-2 border-blue-400'
                                            : 'text-blue-600 border-b-2 border-blue-600'
                                        : theme === 'dark'
                                            ? 'text-gray-400 hover:text-gray-300'
                                            : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab Content */}
                <div className="pb-8">
                    {activeTab === 'TDS SUMMARY' && renderSummaryTab()}
                    {activeTab === 'TDS HISTORY' && renderHistoryTab()}
                    {activeTab === 'TAX FORMS' && renderTaxFormsTab()}
                </div>
            </div>
        </div>
    );
};

export default EmployeeTax;
